import PropTypes from 'prop-types';
import { Box, Typography, Skeleton } from '@mui/material';
import ReportListSearch from './ReportListSearch';
import ReportListItem from './ReportListItem';

const ReportItemSkeleton = () => {
	const randomWidth = `${Math.floor(Math.random() * (60 - 10 + 1) + 15)}%`;

	return (
		<Box
			sx={{
				padding: '9px 15px',
				borderBottom: '0.5px solid',
				borderColor: 'border.light',
			}}
		>
			<Box>
				<Skeleton width={randomWidth} height={20} />
				<Skeleton width='30%' height={18} />
			</Box>
		</Box>
	);
};

const ReportListSkeleton = () => (
	<Box data-testid='report-list-skeleton'>
		{Array.from({ length: 10 }).map((_, index) => (
			<ReportItemSkeleton key={index} />
		))}
	</Box>
);

const ReportList = ({
	reports,
	loading,
	searchQuery,
	setSearchQuery,
	handleSelectedReport,
	totalCount,
}) => {
	const noReports = reports.length === 0;

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
			}}
		>
			<ReportListSearch
				searchQuery={searchQuery}
				setSearchQuery={setSearchQuery}
				disabled={loading || (noReports && !searchQuery)} // if user is actively searching, don't disable
			/>

			{/* Report count display */}
			{totalCount > 0 && (
				<Typography variant='overline' color='text.secondary'>
					{searchQuery
						? `${reports.length} of ${totalCount} report${totalCount === 1 ? '' : 's'}`
						: `${totalCount} report${totalCount === 1 ? '' : 's'} available`}
				</Typography>
			)}

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: 'auto',
					border: '0.5px solid',
					borderColor: 'border.light',
					borderRadius: '8px',
				}}
			>
				{loading && <ReportListSkeleton />}

				{!loading && noReports && (
					<Box
						display='flex'
						justifyContent='center'
						alignItems='center'
						sx={{ height: '100%', width: '100%' }}
					>
						{/* Append text if search returns no results */}
						<Typography color='text.secondary'>
							No reports {`${searchQuery && ' match your search'}`}
						</Typography>
					</Box>
				)}

				{/* Show reports */}
				{!loading &&
					reports.length > 0 &&
					reports.map((report) => (
						<ReportListItem
							key={report.id}
							report={report}
							handleSelectedReport={handleSelectedReport}
						/>
					))}
			</Box>
		</Box>
	);
};

ReportList.propTypes = {
	reports: PropTypes.array.isRequired,
	loading: PropTypes.bool.isRequired,
	searchQuery: PropTypes.string.isRequired,
	setSearchQuery: PropTypes.func.isRequired,
	handleSelectedReport: PropTypes.func.isRequired,
	totalCount: PropTypes.number.isRequired,
};

export default ReportList;
